import React from 'react';
import { useParams } from 'react-router-dom';

const LegalPages: React.FC = () => {
  const { page } = useParams<{ page: string }>();

  const getPageContent = () => {
    switch (page) {
      case 'privacy-policy':
        return {
          title: 'Privacy Policy',
          content: `
            <h2>Information We Collect</h2>
            <p>When you use our invoice generator, we may collect certain information to provide you with our services.</p>
            
            <h3>Personal Information</h3>
            <p>We collect information you provide directly to us, such as when you create an account, generate invoices, or contact us for support.</p>
            
            <h3>Usage Information</h3>
            <p>We automatically collect information about how you use our services, including your IP address, browser type, and usage patterns.</p>
            
            <h2>How We Use Your Information</h2>
            <p>We use the information we collect to provide, maintain, and improve our services, process transactions, and communicate with you.</p>
            
            <h2>Information Sharing</h2>
            <p>We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy.</p>
            
            <h2>Data Security</h2>
            <p>We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>
          `
        };
      
      case 'terms-of-use':
        return {
          title: 'Terms of Use',
          content: `
            <h2>Acceptance of Terms</h2>
            <p>By accessing and using InvoiceGen, you accept and agree to be bound by the terms and provision of this agreement.</p>
            
            <h2>Use License</h2>
            <p>Permission is granted to temporarily use InvoiceGen for personal and commercial invoice generation purposes.</p>
            
            <h2>Disclaimer</h2>
            <p>The materials on InvoiceGen are provided on an 'as is' basis. InvoiceGen makes no warranties, expressed or implied.</p>
            
            <h2>Limitations</h2>
            <p>In no event shall InvoiceGen or its suppliers be liable for any damages arising out of the use or inability to use the materials on InvoiceGen.</p>
            
            <h2>User Responsibilities</h2>
            <p>Users are responsible for ensuring the accuracy of information entered into invoices and compliance with applicable tax laws.</p>
          `
        };

      case 'cookies':
        return {
          title: 'Cookie Policy',
          content: `
            <h2>What Are Cookies</h2>
            <p>Cookies are small text files that are stored on your device when you visit our website.</p>
            
            <h2>How We Use Cookies</h2>
            <p>We use cookies to improve your experience, analyze usage, and provide personalized content.</p>
            
            <h3>Essential Cookies</h3>
            <p>These cookies are necessary for the website to function properly and cannot be disabled.</p>
            
            <h3>Analytics Cookies</h3>
            <p>We use analytics cookies to understand how visitors interact with our website.</p>
            
            <h2>Managing Cookies</h2>
            <p>You can control and manage cookies through your browser settings.</p>
          `
        };

      case 'security':
        return {
          title: 'Security and Personal Data',
          content: `
            <h2>Our Commitment to Security</h2>
            <p>We are committed to protecting your personal data and maintaining the security of our services.</p>
            
            <h2>Data Encryption</h2>
            <p>All data transmitted to and from our servers is encrypted using industry-standard SSL/TLS protocols.</p>
            
            <h2>Access Controls</h2>
            <p>We implement strict access controls to ensure that only authorized personnel can access your data.</p>
            
            <h2>Regular Security Audits</h2>
            <p>We regularly conduct security audits and assessments to identify and address potential vulnerabilities.</p>
            
            <h2>Incident Response</h2>
            <p>In the unlikely event of a security incident, we have procedures in place to respond quickly and notify affected users.</p>
          `
        };

      default:
        return {
          title: 'Page Not Found',
          content: '<p>The requested page could not be found.</p>'
        };
    }
  };

  const { title, content } = getPageContent();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-12 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              {title}
            </h1>
            
            <div 
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: content }}
            />
            
            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Last updated: January 15, 2024
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPages;